import { useEffect } from 'react';
import { RESEARCH_MAX_ATTEMPTS, type Note, type ResearchState } from '@almanac/contracts';

const LABELS: Record<ResearchState, string> = {
  queued: 'queued',
  running: 'researching',
  retry_scheduled: 'retry scheduled',
  failed: 'failed',
  completed: 'ready',
};
const TONES: Record<ResearchState, string> = {
  queued: 'bg-muted text-muted-foreground',
  running: 'bg-primary/10 text-primary',
  retry_scheduled: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  failed: 'bg-destructive/10 text-destructive',
  completed: 'bg-muted text-muted-foreground',
};
const POLL_MS = 30_000;

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Attempt, start time, and next retry or last error for a research request. */
export function researchProgress(note: Note): string[] {
  const state = note.research_state;
  if (!state || state === 'completed') return [];
  const parts: string[] = [];
  if (note.research_attempts > 0 || state === 'running')
    parts.push(`attempt ${note.research_attempts} of ${RESEARCH_MAX_ATTEMPTS}`);
  if (state === 'running' && note.research_claimed_at)
    parts.push(`started ${when(note.research_claimed_at)}`);
  if (state === 'retry_scheduled' && note.research_next_attempt_at)
    parts.push(`retry ${when(note.research_next_attempt_at)}`);
  if (state !== 'running' && note.research?.error) parts.push(`error: ${note.research.error}`);
  return parts;
}

/** The research state pill: derived lifecycle plus progress, so stalls are visible. */
export function ResearchStatePill({ note }: { note: Note }) {
  const state = note.research_state;
  // Archived requests sit outside the queue, so their archive status is the honest label.
  if (!state || note.status === 'archived')
    return (
      <span className="rounded-full bg-muted px-2 py-1 text-sm text-muted-foreground">
        {note.status}
      </span>
    );
  const progress = researchProgress(note);
  return (
    <>
      <span className={`rounded-full px-2 py-1 text-sm ${TONES[state]}`}>{LABELS[state]}</span>
      {progress.length > 0 ? (
        <span className="line-clamp-1 max-w-full text-sm text-muted-foreground">
          {progress.join(' · ')}
        </span>
      ) : null}
    </>
  );
}

const OPEN_STATES = new Set<ResearchState>(['queued', 'running', 'retry_scheduled']);

/**
 * Refresh every 30s while any research request is queued, running, or waiting
 * on a scheduled retry (so the page follows it once the retry is due), pausing
 * while the tab is hidden and catching up when it returns (as PackagePanel does).
 */
export function useResearchPolling(notes: Note[], refresh: () => Promise<void>): void {
  const active = notes.some(
    (note) =>
      note.status !== 'archived' &&
      note.research_state !== null &&
      OPEN_STATES.has(note.research_state),
  );
  useEffect(() => {
    if (!active) return;
    let inFlight = false;
    const load = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await refresh();
      } finally {
        inFlight = false;
      }
    };
    const interval = window.setInterval(() => void load(), POLL_MS);
    const visibility = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [active, refresh]);
}
